import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MessageSquare, CheckSquare, Trash2, FolderKanban, Loader2, Users, Pencil, Check, CheckCheck } from "lucide-react";
import UsersManager from "@/components/UsersManager";

function MemberPicker({ users, selected, onToggle }) {
  const list = users || [];
  const chosen = selected || [];
  return (
    <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2" data-testid="member-picker">
      {list.map((u) => {
        const checked = chosen.includes(u.id);
        return (
          <div
            key={u.id}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(u.id);
            }}
            className="flex items-center gap-3 text-sm cursor-pointer select-none rounded-md px-2 py-1.5 hover:bg-slate-50 transition-colors"
            data-testid={`member-option-${u.id}`}
          >
            <span
              className={cn(
                "h-4 w-4 rounded-sm border border-slate-400 flex items-center justify-center shrink-0 transition-colors",
                checked && "bg-slate-900 text-white border-slate-900"
              )}
            >
              {checked && <Check className="h-3 w-3" />}
            </span>
            <span className="text-slate-900">{u.name}</span>
            <span className="text-slate-400 text-xs">
              {u.email} · {u.role}
            </span>
          </div>
        );
      })}
      {list.length === 0 && (
        <p className="text-sm text-slate-400 px-2 py-1.5">No users yet. Add members from the Team dialog.</p>
      )}
    </div>
  );
}

function ProjectEditDialog({ project, users, reload, refreshUsers }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || "");
  const [memberIds, setMemberIds] = useState(project.members || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description || "");
      setMemberIds(project.members || []);
      if (refreshUsers) refreshUsers();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setMemberIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}`, { name, description, member_ids: memberIds });
      toast.success("Project updated");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="absolute top-4 right-11 text-slate-300 hover:text-slate-700 transition-colors"
          aria-label="Edit project"
          data-testid={`edit-project-${project.id}`}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit project</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid={`project-edit-form-${project.id}`}>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="edit-project-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-project-desc-input" />
          </div>
          <div className="space-y-2">
            <Label>Members (who can access this project)</Label>
            <MemberPicker users={users} selected={memberIds} onToggle={toggle} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid={`project-edit-submit-${project.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      /* non-admins won't need this */
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) loadUsers();
    const refresh = () => load();
    window.addEventListener("notifications-updated", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("notifications-updated", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setMemberIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", { name, description, member_ids: memberIds });
      toast.success("Project created");
      setName("");
      setDescription("");
      setMemberIds([]);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/read-all", {});
      window.dispatchEvent(new Event("notifications-updated"));
      toast.success("Everything marked as read");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const hasUnread = (projects || []).some((p) => p.unread > 0 || p.unread_items > 0);

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its tasks & discussions?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-400">Workspace</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Projects
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {hasUnread && (
            <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all as read
            </Button>
          )}
        {isAdmin && (
          <div className="flex items-center gap-3">
            <UsersManager onUsersChanged={loadUsers} />
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) loadUsers(); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-slate-900 hover:bg-slate-700 text-white transition-all active:scale-[0.98]"
                  data-testid="new-project-btn"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> New project
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create project</DialogTitle>
                </DialogHeader>
                <form onSubmit={create} className="space-y-4" data-testid="project-form">
                  <div className="space-y-2">
                    <Label htmlFor="p-name">Name</Label>
                    <Input
                      id="p-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Website redesign"
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="p-desc">Description</Label>
                    <Textarea
                      id="p-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What is this project about?"
                      data-testid="project-desc-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Members (who can access this project)</Label>
                    <MemberPicker users={users} selected={memberIds} onToggle={toggle} />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="bg-slate-900 hover:bg-slate-700 text-white"
                      data-testid="project-submit-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
        </div>
      </div>

      {projects === null ? (
        <div className="text-slate-400 py-20 text-center">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div
          className="border border-dashed border-slate-300 rounded-xl bg-white py-16 flex flex-col items-center text-center"
          data-testid="projects-empty"
        >
          <FolderKanban className="h-10 w-10 text-slate-300" />
          <p className="mt-4 text-slate-900 font-medium">No projects yet</p>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Create your first project and add members to get started."
              : "You haven't been added to any projects yet. Ask an admin to add you."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
          {projects.map((p) => (
            <div
              key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className={cn(
                "group relative bg-white border rounded-xl p-6 transition-all duration-200 hover:shadow-md hover:-translate-y-[2px] animate-fade-in-up cursor-pointer",
                p.unread > 0 || p.unread_items > 0 ? "border-red-400 ring-1 ring-red-200" : "border-slate-200 hover:border-slate-300"
              )}
              data-testid={`project-card-${p.id}`}
            >
              <h3 className="font-heading text-lg font-medium text-slate-900 pr-16">{p.name}</h3>
              <p className="text-sm text-slate-500 mt-2 line-clamp-2 min-h-[2.5rem]">
                {p.description || "No description"}
              </p>
              <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                {p.my_access !== "guest" && (
                <span className="inline-flex items-center gap-1.5">
                  <CheckSquare className="h-4 w-4" /> {p.task_count}
                </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4" /> {p.discussion_count}
                </span>
                {p.my_access !== "guest" && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> {(p.members || []).length}
                </span>
                )}
                {(p.unread_items > 0 || p.unread > 0) && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white bg-red-500 rounded-full px-2 py-0.5"
                    title={`${p.unread_items || 0} unread tasks/discussions · ${p.unread || 0} new notifications`}
                    data-testid={`project-unread-${p.id}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                    {p.unread_items > 0 ? `${p.unread_items} unread` : `${p.unread} new`}
                  </span>
                )}
              </div>
              {isAdmin && (
                <>
                  <ProjectEditDialog project={p} users={users} reload={load} refreshUsers={loadUsers} />
                  <button
                    onClick={(e) => remove(p.id, e)}
                    className="absolute top-4 right-4 text-slate-300 hover:text-red-500 transition-colors"
                    aria-label="Delete project"
                    data-testid={`delete-project-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
